import React, { useEffect, useState } from 'react'
import { useRouteError } from 'react-router-dom'
import './errorBoundary.scss'

/**
 * Global error handling + stale-deploy chunk recovery — all in one file.
 *
 * After a deploy the hashed chunk files change and the old ones are removed, so
 * a browser still running the previous build 404s when it lazy-loads a now
 * missing chunk → `ChunkLoadError`. The cure: wipe any client-side cache that
 * could still serve the stale build, then reload once to fetch the fresh one.
 *
 * Exports:
 *   - default `ErrorBoundary`  — wrap the app (in App.tsx). Class boundary that
 *     catches render errors outside the routed tree (providers, layout shell).
 *   - `RouteErrorBoundary`     — the router's `errorElement` (in appRouter).
 *     A React Router *data* router routes route render errors (incl. a lazy
 *     route's ChunkLoadError) here, NOT to the class boundary — so both exist.
 *   - `clearChunkRecoveryFlag` — call once on successful boot (in index.tsx) to
 *     re-arm the one-shot reload guard.
 */

/* ------------------------------------------------------------------ helpers */

/** True when an error looks like a failed code-split JS/CSS chunk fetch. */
const isChunkLoadError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false
  const name = (error as { name?: string }).name ?? ''
  const message = (error as { message?: string }).message ?? ''
  return (
    name === 'ChunkLoadError' ||
    /loading chunk \S+ failed/i.test(message) ||
    /loading css chunk \S+ failed/i.test(message) ||
    /failed to fetch dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message)
  )
}

/**
 * Clear all client-side caches so a reload can't be served the stale build:
 * the Cache Storage API (PWA / service-worker caches) and any registered
 * service workers. Best-effort — failures are swallowed so recovery proceeds.
 */
const clearAppCaches = async (): Promise<void> => {
  try {
    if ('caches' in window) {
      const keys = await window.caches.keys()
      await Promise.all(keys.map((key) => window.caches.delete(key)))
    }
  } catch {
    /* ignore — clearing caches is best-effort */
  }
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map((reg) => reg.unregister()))
    }
  } catch {
    /* ignore — unregistering is best-effort */
  }
}

// One-shot guard so a failed-then-reloaded chunk can't loop forever.
const RELOAD_FLAG = 'chunk-recovery-reloaded'

/**
 * Recover from a chunk error: clear caches, then reload once to pull the fresh
 * build. Returns `true` if it kicked off a reload, `false` if it deliberately
 * didn't (offline, or it already reloaded once) — caller shows a fallback then.
 */
const recoverFromChunkError = (): boolean => {
  if (!window.navigator.onLine) return false // offline → can't refetch
  if (window.sessionStorage.getItem(RELOAD_FLAG) === 'true') return false // already retried
  window.sessionStorage.setItem(RELOAD_FLAG, 'true')
  void clearAppCaches().finally(() => window.location.reload())
  return true
}

/** Re-arm the one-shot guard once the app has booted successfully. */
export const clearChunkRecoveryFlag = (): void => {
  window.sessionStorage.removeItem(RELOAD_FLAG)
}

/* ----------------------------------------------------------------- fallback */

/** Shared fallback. `loading` = chunk recovery (cache-clear + reload) in flight. */
const ErrorFallback: React.FC<{ variant?: 'error' | 'loading' }> = ({
  variant = 'error'
}) => {
  if (variant === 'loading') {
    return (
      <div className="app-error app-error--loading">
        <div className="app-error__card">
          <h1 className="app-error__title">Updating…</h1>
          <p className="app-error__message">
            A new version is available — loading the latest assets.
          </p>
        </div>
      </div>
    )
  }
  return (
    <div className="app-error">
      <div className="app-error__card">
        <div className="app-error__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12 2 1 21h22L12 2Z"
              fill="currentColor"
              fillOpacity="0.12"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinejoin="round"
            />
            <path
              d="M12 9v5"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
            <circle cx="12" cy="17" r="1.1" fill="currentColor" />
          </svg>
        </div>
        <h1 className="app-error__title">Something went wrong</h1>
        <p className="app-error__message">
          An unexpected error occurred. Reloading the page usually fixes it.
        </p>
        <div className="app-error__actions">
          <button
            type="button"
            className="app-error__btn app-error__btn--primary"
            // Clear caches first so the reload can't be served the stale build.
            onClick={() => {
              void clearAppCaches().finally(() => window.location.reload())
            }}
          >
            Reload page
          </button>
          <button
            type="button"
            className="app-error__btn"
            onClick={() => {
              window.location.href = '/'
            }}
          >
            Go to home
          </button>
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------- router errorElement */

/**
 * React Router `errorElement` for the root route — catches errors thrown while
 * rendering ANY route (including a lazy route's ChunkLoadError). On a chunk
 * error it clears caches and reloads once (showing "Updating…"); anything else,
 * or a repeat failure, falls through to the graceful error card.
 */
export const RouteErrorBoundary: React.FC = () => {
  const error = useRouteError()
  const chunk = isChunkLoadError(error)
  const [recovering, setRecovering] = useState(chunk)

  useEffect(() => {
    if (chunk && !recoverFromChunkError()) setRecovering(false)
  }, [chunk])

  return <ErrorFallback variant={recovering ? 'loading' : 'error'} />
}

/* --------------------------------------------------- global class boundary */

interface Props {
  children: React.ReactNode
}
interface State {
  hasError: boolean
  /** A chunk error is being recovered (cache-clear + reload in flight). */
  recovering: boolean
}

/**
 * App-wide error boundary. Catches any render error in the tree below it and,
 * instead of a blank screen, either recovers from a stale-deploy ChunkLoadError
 * (clear caches + reload once, showing "Updating…") or shows a graceful
 * fallback. Covers everything outside the routed tree (providers, layout shell).
 */
class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, recovering: false }

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true }
  }

  componentDidCatch(error: unknown): void {
    if (isChunkLoadError(error) && recoverFromChunkError()) {
      this.setState({ recovering: true })
    }
  }

  render(): React.ReactNode {
    const { hasError, recovering } = this.state
    if (hasError) {
      return <ErrorFallback variant={recovering ? 'loading' : 'error'} />
    }
    return this.props.children
  }
}

export default ErrorBoundary
